import { ArrowSquareOutIcon, KeyboardIcon, MagnifyingGlassIcon, SignOutIcon } from '@phosphor-icons/react'
import { Avatar, CommandPalette, Keys, Menu, ShardMark, toast, useHotkeys, type Hotkey } from '@robotensor/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { isProfessional, permissionsFor, teamOnly, tokenGrants } from '../lib/access.ts'
import { api, client } from '../lib/api.ts'
import { useSession } from '../lib/auth.tsx'
import { acceptHandoff, usePendingHandoffs } from '../lib/handoff.ts'
import { useControlModes } from '../sim/modes.ts'
import { BarNotices } from './Notices.tsx'
import { Notifications } from './Notifications.tsx'
import { activeNav, visibleNav } from './nav.tsx'
import { PhonePill } from './PhonePill.tsx'
import { usePalette } from './palette.tsx'
import { ShortcutsDialog, useShortcutsOpener } from './Shortcuts.tsx'
import { useSite } from './site.tsx'

const RECENT_KEY = 'rt.palette.recent'

/**
 * The contributor app's frame (N1): the bar on every page (logo, Home · Robot · Recordings · Wallet, the phone pill,
 * the bell, the avatar) and, below 820 px, the four sections as a bottom bar like the phone app's tabs. The robot
 * session (/robot/run/…) is full screen: no bar, only the scene. Ops keeps the console shell (Shell.tsx).
 */
export function AppFrame(): ReactElement {
  const site = useSite()
  const session = useSession()
  const user = session!.user
  const me = useQuery({ queryKey: ['me'], queryFn: api.me.get, staleTime: 60_000 })
  const perms = useMemo(() => (me.data ? new Set(me.data.permissions) : permissionsFor(user.roles, tokenGrants(session?.accessToken))), [me.data, user.roles, session?.accessToken])
  const professional = isProfessional(me.data?.roles ?? user.roles)
  const team = teamOnly(me.data?.roles ?? user.roles)
  const nav = useMemo(() => visibleNav(site.nav, perms, professional, team), [site.nav, perms, professional, team])
  const bar = nav.filter((n) => n.bar !== false)
  // phones get the same four as a bottom bar; Account is the avatar in the top bar there too
  const tabs = nav.filter((n) => n.bottom)
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  useShortcutsOpener(useCallback(() => setShortcutsOpen(true), []))
  const [recent, setRecent] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[]
    } catch {
      return []
    }
  })

  const signOut = useCallback(async () => {
    await client.signOut()
    queryClient.clear()
    void navigate({ to: '/signin', search: { reason: 'signed-out' } })
  }, [navigate, queryClient])

  const { commands, resolve, fallback } = usePalette({ nav, perms, userId: user.id, onShortcuts: () => setShortcutsOpen(true), onSignOut: () => void signOut(), themes: false })
  const current = activeNav(location.pathname, nav)
  const fullScreen = location.pathname.startsWith('/robot/run/')

  const hotkeys: Hotkey[] = useMemo(
    () => [
      { keys: 'mod+k', allowInInput: true, handler: () => setPaletteOpen((o) => !o) },
      { keys: '?', handler: () => setShortcutsOpen(true) },
      {
        keys: '/',
        handler: () => {
          const el = document.querySelector<HTMLInputElement>('[data-page-search]')
          if (el) el.focus()
          else setPaletteOpen(true)
        },
      },
      ...nav.filter((n) => n.keys).map((n) => ({ keys: n.keys!, handler: () => void navigate({ to: n.to }) })),
    ],
    [nav, navigate],
  )
  useHotkeys(hotkeys)

  // Land keyboard and screen-reader users in the content after navigating, at the top.
  useEffect(() => {
    const main = document.getElementById('main')
    if (main && !main.contains(document.activeElement)) main.focus({ preventScroll: true })
    window.scrollTo({ top: 0 })
  }, [location.pathname])

  const onRun = useCallback((cmd: { id: string }) => {
    setRecent((r) => {
      const next = [cmd.id, ...r.filter((x) => x !== cmd.id)].slice(0, 6)
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next))
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  const displayName = me.data?.display_name ?? user.display_name

  const account = (
    <Menu
      align="end"
      trigger={
        <button type="button" className="a-avatar w-avatar" aria-label={`Account: ${displayName}`}>
          <Avatar name={displayName} src={me.data?.avatar_url} size={36} />
        </button>
      }
      items={[
        { heading: user.email ?? displayName, key: 'h' },
        ...site.accountLinks.map((a) => ({ key: a.key, label: a.label, icon: a.icon, keys: a.keys, onSelect: () => void navigate({ to: a.to as never }) })),
        ...(site.helpLinks?.length
          ? [
              { separator: true as const, key: 'sh' },
              ...site.helpLinks.map((h) => ({
                key: h.key,
                label: (
                  <span className="a-menu__out">
                    {h.label} <ArrowSquareOutIcon size={12} aria-label="opens in a new tab" />
                  </span>
                ),
                icon: h.icon,
                onSelect: () => void window.open(h.href, '_blank', 'noopener,noreferrer'),
              })),
            ]
          : []),
        { separator: true, key: 's1' },
        { key: 'search', label: 'Search', icon: <MagnifyingGlassIcon />, keys: 'mod+k', onSelect: () => setPaletteOpen(true) },
        { key: 'keys', label: 'Keyboard shortcuts', icon: <KeyboardIcon />, keys: '?', onSelect: () => setShortcutsOpen(true) },
        { key: 'out', label: 'Sign out', icon: <SignOutIcon />, onSelect: () => void signOut() },
      ]}
    />
  )

  const overlays = (
    <>
      <HandoffWatcher />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} commands={commands} resolve={resolve} fallback={fallback} recent={recent} onRun={onRun} groupOrder={['Go to', 'Actions', 'Preferences', 'Help', 'Account']} />
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} nav={nav} />
    </>
  )

  if (fullScreen)
    return (
      <div className="a-app w-app" data-full="">
        <main id="main" tabIndex={-1} className="w-full">
          <Outlet />
        </main>
        {overlays}
      </div>
    )

  return (
    <div className="a-app w-app">
      <a className="a-skip" href="#main">
        Skip to content
      </a>
      <header className="a-top w-appbar">
        <div className="a-top__in">
          <Link to="/home" className="a-brand" aria-label="Horizon, Home">
            <ShardMark size={26} />
            <span>Horizon</span>
            {import.meta.env.DEV ? <span className="a-env">Dev</span> : null}
          </Link>
          <nav className="a-nav" aria-label="Main navigation">
            {bar.map((n) => (
              <Link key={n.id} to={n.to} className="a-nav__item" aria-current={current?.id === n.id ? 'page' : undefined} activeOptions={{ exact: n.to === '/home' }}>
                <n.icon weight={current?.id === n.id ? 'fill' : 'regular'} aria-hidden />
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="a-top__actions">
            <button type="button" className="a-search" onClick={() => setPaletteOpen(true)} aria-label="Search (Command K)">
              <MagnifyingGlassIcon aria-hidden />
              <span className="a-search__text">Search</span>
              <Keys keys="mod+k" />
            </button>
            <PhonePill />
            <Notifications />
            {account}
          </div>
        </div>
      </header>
      <BarNotices />
      <main id="main" tabIndex={-1} className="a-main w-main">
        <Outlet />
      </main>
      <nav className="w-tabs" aria-label="Main navigation">
        {tabs.map((n) => (
          <Link key={n.id} to={n.to} className="w-tabs__item" aria-current={current?.id === n.id ? 'page' : undefined} activeOptions={{ exact: n.to === '/home' }}>
            <n.icon weight={current?.id === n.id ? 'fill' : 'regular'} aria-hidden />
            <span>{n.short ?? n.label}</span>
          </Link>
        ))}
      </nav>
      {overlays}
    </div>
  )
}

/**
 * When staff have switched on "Continue on computer", a task sent from the person's phone opens in this tab by
 * itself. Off (the default), this renders nothing and asks the server nothing.
 */
function HandoffWatcher(): null {
  const { modes } = useControlModes()
  const on = modes.has('phone_handoff')
  const pending = usePendingHandoffs(on)
  const navigate = useNavigate()
  const seen = useRef(new Set<string>())
  useEffect(() => {
    if (!on || document.visibilityState !== 'visible') return
    const h = pending.data?.find((x) => !seen.current.has(x.id))
    if (!h) return
    seen.current.add(h.id)
    void acceptHandoff(h.id)
      .then((next) => {
        toast({ title: 'Opened from your phone', description: h.title, tone: 'live' })
        void navigate({ href: next })
      })
      .catch(() => undefined)
  }, [on, pending.data, navigate])
  return null
}
